// The two auth-backend calls these screens make. `POST /auth/request` and
// `GET /auth/consume` per PRDs/18-figma-surfaces/progress/progress-auth-backend.txt
// §Endpoints. `credentials: "include"` is required on `/consume` — it is the
// call that receives the `Set-Cookie`, and in dev the API and Vite run on
// different ports so the browser needs the explicit opt-in to store it.

const API_BASE =
  (import.meta.env?.VITE_API_BASE as string | undefined) ??
  "http://localhost:8000";

/** RFC-7807 `application/problem+json` shape (`backend/api/errors.py`). */
export type ProblemDetail = {
  type: string;
  title: string;
  status: number;
  detail?: string | null;
};

export class AuthApiError extends Error {
  readonly problem: ProblemDetail | null;

  constructor(problem: ProblemDetail | null, fallbackMessage: string) {
    super(problem?.title ?? fallbackMessage);
    this.problem = problem;
  }
}

async function readProblem(res: Response): Promise<ProblemDetail | null> {
  try {
    const body = (await res.json()) as Partial<ProblemDetail>;
    if (typeof body.title === "string") return body as ProblemDetail;
    return null;
  } catch {
    return null; // Non-JSON body (network failure, proxy error page, ...).
  }
}

/** Always answers 202 — never branch UI on whether the address is known. */
export async function requestSignInLink(email: string): Promise<void> {
  const res = await fetch(`${API_BASE}/auth/request`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    throw new AuthApiError(await readProblem(res), "Could not request a sign-in link");
  }
}

export type SessionUser = { email: string; org_slug: string };

/** Exchanges a magic-link token for a session; the backend sets the cookie. */
export async function consumeMagicToken(token: string): Promise<SessionUser> {
  const url = `${API_BASE}/auth/consume?token=${encodeURIComponent(token)}`;
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) {
    throw new AuthApiError(await readProblem(res), "Could not verify that link");
  }
  return (await res.json()) as SessionUser;
}
